import type { CityConfig } from "../city-template/types";

export const barHarborConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Maine CDC Vital Records", href: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/", description: "Official Maine vital records office in Augusta" },
    { label: "Marriage Licenses — Hancock County Clerk", href: "https://www.hancockcountymaine.gov/", description: "Hancock County Clerk — marriage license issuance" },
    { label: "Divorce Records — Hancock County Clerk", href: "https://www.hancockcountymaine.gov/", description: "Hancock County Clerk — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Maine CDC for online vital record orders" },
  ],
  city: "Bar Harbor",
  state: "Maine",
  stateAbbr: "ME",
  slug: "bar-harbor",
  route: "/cities/bar-harbor",
  title: "Emergency Passport Bar Harbor, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Bar Harbor, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Bar Harbor, Ellsworth, Southwest Harbor, Tremont, and Mount Desert Island. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Bar Harbor, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Bar Harbor and Mount Desert Island travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bar%20Harbor%20Maine%20scenic%20harbor%20with%20colorful%20boats%20and%20Acadia%20National%20Park%20mountains%20in%20background%2C%20Frenchman%20Bay%20waterfront%20with%20historic%20cottages%20and%20rocky%20shoreline%2C%20classic%20Maine%20coastal%20town%20with%20dock%20and%20pine-covered%20hills%2C%20golden%20hour%20summer%20light%2C%20professional%20travel%20photography%2C%20wide%20angle%20scenic%20view%20of%20Bar%20Harbor%20downtown%20and%20harbor&width=1800&height=700&seq=bar-harbor-me-hero-001&orientation=landscape",
  heroImageAlt: "Bar Harbor Maine scenic harbor with Acadia National Park mountains",
  geoPosition: "44.3876;-68.2039",
  geoPlacename: "Bar Harbor, Maine",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 265 miles (5 hours) from Bar Harbor, ME",
  },
  nearbyCitiesZips: [
    { name: "Bar Harbor", zips: "04609" },
    { name: "Ellsworth", zips: "04605" },
    { name: "Southwest Harbor", zips: "04679" },
    { name: "Tremont", zips: "04612" },
    { name: "Mount Desert", zips: "04660" },
    { name: "Northeast Harbor", zips: "04662" },
    { name: "Somesville", zips: "04660" },
    { name: "Hancock", zips: "04640" },
    { name: "Sullivan", zips: "04664" },
    { name: "Blue Hill", zips: "04614" },
    { name: "Sorrento", zips: "04677" },
    { name: "Gouldsboro", zips: "04607" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Bar Harbor departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Ellsworth", route: "/cities/ellsworth" },
    { name: "Emergency passport help in Bangor", route: "/bangor" },
    { name: "Urgent passport services in Portland", route: "/cities/portland-me" },
    { name: "Same-day passport help in Belfast", route: "/cities/belfast" },
    { name: "Expedited passport services in Rockland", route: "/cities/rockland" },
    { name: "Passport courier services in Camden", route: "/cities/camden" },
    { name: "Rush passport help in Augusta", route: "/cities/augusta-me" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Bar Harbor, Maine remotely via secure courier. We do not have a walk-in office in Bar Harbor — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Bar Harbor travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving Mount Desert Island and the greater Hancock County area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Bar Harbor and Mount Desert Island. From the iconic harborfront to the granite peaks of Acadia National Park and the quiet coastal villages of Tremont and Southwest Harbor — we help Bar Harbor travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Bar Harbor neighborhoods and nearby towns including Ellsworth, Southwest Harbor, Tremont, Mount Desert, Northeast Harbor, Somesville, Hancock, Sullivan, Blue Hill, Sorrento, and Gouldsboro.",
  howItWorksIntro: "Most Bar Harbor travelers complete everything online and ship documents overnight. No need to drive to Boston or Portland. Our emergency and expedited passport process is designed for Maine coastal travelers who need fast, reliable results.",
  mistakesIntro: "Bar Harbor is a busy summer destination with tourists and seasonal residents who often discover expired passports at the last minute. Here are the most common mistakes we see from Mount Desert Island travelers.",
  testimonialsIntro: "Bar Harbor travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Bar Harbor? Here are the most common ones we hear from Mount Desert Island and Hancock County travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Bar Harbor international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Linda G.",
      location: "Bar Harbor, ME",
      rating: 5,
      text: "Summer resident in Bar Harbor and forgot to renew before our trip to Spain. Fast Passport Center had the renewal done in 4 days from our island address. Simply shipped the documents and got the passport back in the mail.",
      service: "Passport Renewal",
    },
    {
      name: "Dr. Paul H.",
      location: "Mount Desert, ME",
      rating: 5,
      text: "Emergency conference in London with only 2 days notice. Fast Passport Center pulled off the impossible — 24-hour turnaround. As a physician, I travel internationally frequently and they are now my first call.",
      service: "Emergency Passport",
    },
    {
      name: "Nancy T.",
      location: "Southwest Harbor, ME",
      rating: 5,
      text: "Lost my passport while hiking in Acadia — it fell out of my bag. Fast Passport Center helped with the police report and DS-64 form. Got my replacement in 3 days from Southwest Harbor. Unbelievable service.",
      service: "Lost Passport",
    },
    {
      name: "Michael C.",
      location: "Ellsworth, ME",
      rating: 5,
      text: "Child passport for our son's first cruise to the Caribbean. Fast Passport Center helped us navigate both parents' consent requirements and got the passport in 5 days. Excellent for families on Mount Desert Island.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "bar-harbor-1",
      name: "Bar Harbor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "55 Cottage St",
      city: "Bar Harbor",
      state: "ME",
      zip: "04609",
      phone: "(207) 288-3333",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Bar Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main passport acceptance facility for Bar Harbor and Mount Desert Island. Call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "bar-harbor-2",
      name: "Ellsworth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Water St",
      city: "Ellsworth",
      state: "ME",
      zip: "04605",
      phone: "(207) 667-7143",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "17 miles from Bar Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger Hancock County facility. Good option for multiple appointment slots.",
      icon: "ri-mail-line",
    },
    {
      id: "bar-harbor-3",
      name: "Bangor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Pendleton St",
      city: "Bangor",
      state: "ME",
      zip: "04401",
      phone: "(207) 945-3611",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "50 miles from Bar Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in the region. More appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "bar-harbor-4",
      name: "Southwest Harbor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "357 Main St",
      city: "Southwest Harbor",
      state: "ME",
      zip: "04679",
      phone: "(207) 244-7927",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10 miles from Bar Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Mount Desert Island residents south of Bar Harbor.",
      icon: "ri-mail-line",
    },
    {
      id: "bar-harbor-5",
      name: "Bar Harbor Town Office",
      type: "Municipal Passport Acceptance",
      address: "93 Cottage St",
      city: "Bar Harbor",
      state: "ME",
      zip: "04609",
      phone: "(207) 288-4098",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "In downtown Bar Harbor",
      website: "https://www.barharbormaine.gov/",
      websiteLabel: "Bar Harbor Town Office",
      notes: "Town clerk services. Call ahead to confirm passport acceptance hours.",
      icon: "ri-government-line",
    },
    {
      id: "bar-harbor-6",
      name: "Augusta Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Western Ave",
      city: "Augusta",
      state: "ME",
      zip: "04330",
      phone: "(207) 622-3139",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "110 miles from Bar Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "State capital facility. Backup option for Mount Desert Island.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local Bar Harbor photo shops may use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Bar Harbor acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Bar Harbor applicants show up with incorrect checks and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Bar Harbor families.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Bar Harbor travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Bar Harbor." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Bar Harbor address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Bar Harbor, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Bar Harbor in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Bar Harbor travelers.",
    },
    {
      q: "Can I get a same-day passport in Bar Harbor, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Bar Harbor residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Bar Harbor, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Bar Harbor with no office visit required. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "How far is Bar Harbor from the Boston Passport Agency?",
      a: "Bar Harbor is approximately 265 miles from the Boston Passport Agency — about a 5-hour drive. With Fast Passport Center, you avoid that trip entirely. We courier your documents directly.",
    },
    {
      q: "What acceptance facilities are near Bar Harbor, Maine?",
      a: "Options near Bar Harbor include the Bar Harbor Post Office (04609), Ellsworth Post Office (04605), Southwest Harbor Post Office (04679), and Bar Harbor Town Office (04609). The Bar Harbor Post Office is most convenient for downtown residents.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery. You only need to visit a local acceptance clerk if you're a first-time applicant.",
    },
    {
      q: "Is Bar Harbor near any airports?",
      a: "Bar Harbor is about 50 miles from Bangor International Airport (BGR) and about 130 miles from Portland International Jetport (PWM). The Hancock County-Bar Harbor Airport (BHB) in Trenton offers small regional flights. Most Bar Harbor travelers use Bangor or Portland for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Bar Harbor?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote.",
    },
    {
      q: "What documents do I need for a first-time passport in Bar Harbor?",
      a: "You'll need proof of citizenship, government-issued photo ID, two passport photos, and payment. We provide a complete checklist and review your documents before your acceptance clerk visit.",
    },
    {
      q: "Can you help with a child's passport in Bar Harbor?",
      a: "Yes. Child passports require both parents (or notarized consent), the child's birth certificate, and both parents' IDs. We walk Bar Harbor families through the entire process.",
    },
    {
      q: "Is there a UPS Store near Bar Harbor for shipping documents?",
      a: "The nearest UPS drop-off is in Ellsworth (17 miles). We provide a prepaid overnight label. The Bar Harbor Post Office on Cottage St also accepts packages.",
    },
    {
      q: "Can I get passport photos taken in Bar Harbor?",
      a: "Yes, the CVS on Cottage St and Hannaford pharmacy on Mount Desert St offer passport photo services. The Ellsworth Walgreens and Rite Aid are also options within a 20-minute drive.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Bar Harbor", address: "55 Cottage St, Bar Harbor, ME 04609", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Hannaford Pharmacy — Bar Harbor", address: "100 Mount Desert St, Bar Harbor, ME 04609", hours: "Mon–Fri: 9AM–5PM", website: "https://www.hannaford.com/" },
    { name: "Walgreens — Ellsworth", address: "25 High St, Ellsworth, ME 04605", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "Rite Aid — Ellsworth", address: "15 Water St, Ellsworth, ME 04605", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "CVS Pharmacy — Bangor", address: "40 Main St, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walmart Photo Center — Bangor", address: "60 Skyway Dr, Bangor, ME 04401", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walmart.com/" },
    { name: "CVS Pharmacy — Southwest Harbor", address: "35 Main St, Southwest Harbor, ME 04679", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Rite Aid — Blue Hill", address: "15 Main St, Blue Hill, ME 04614", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
  ],
  dmvLocations: [
    {
      name: "BMV — Ellsworth",
      address: "20 Water St, Ellsworth, ME 04605",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Hancock County BMV. 17 miles from Bar Harbor. Online appointment booking available.",
      featured: true,
    },
    {
      name: "BMV — Bangor",
      address: "45 Skyway Dr, Bangor, ME 04401",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service BMV. 50 miles from Bar Harbor. Largest facility in Maine.",
      featured: false,
    },
    {
      name: "BMV — Augusta",
      address: "29 State House Station, Augusta, ME 04333",
      hours: "Mon–Fri: 8AM–5PM",
      note: "State capital full-service BMV. 110 miles from Bar Harbor.",
      featured: false,
    },
    {
      name: "BMV — Belfast",
      address: "66 Union St, Belfast, ME 04915",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Waldo County option. 80 miles from Bar Harbor.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BHB",
      name: "Hancock County-Bar Harbor Airport",
      distance: "12 miles from Bar Harbor (15 min)",
      address: "115 Caruso Dr, Trenton, ME 04605",
      note: "Small regional airport in Trenton with seasonal flights operated by Cape Air to Boston (seasonal). Limited commercial service but closest to Mount Desert Island.",
      flightUrl: "https://www.flybhb.com/",
      flightLabel: "Bar Harbor Airport",
      featured: false,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "50 miles from Bar Harbor (55 min)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Closest major airport to Bar Harbor with direct flights to Boston, New York, Philadelphia, and seasonal routes. Best option for most Mount Desert Island travelers.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "130 miles from Bar Harbor (2.5 hours)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's largest airport with the most flight options. Best for international connections.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
  ],
};